import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import { GoArrowUpRight } from "react-icons/go";

// Renders one article's body (an array of blocks from helpCenter.js).
// Block types: p | h | steps | list | callout | faq
//
// Inline links: any text may contain [label](url) markdown-style links.
// External (http...) open in a new tab; internal (/path) use the router.

const linkClass =
  "text-[#3AB1F6] font-medium underline underline-offset-2 hover:text-[#2196e3] transition-colors whitespace-nowrap";

const LinkArrow = () => (
  <GoArrowUpRight className="inline-block size-[0.9em] ml-0.5 align-[-0.05em]" />
);

const renderInline = (text) => {
  if (typeof text !== "string" || !text.includes("[")) return text;
  const pattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const label = match[1];
    const url = match[2];
    if (/^https?:\/\//i.test(url)) {
      parts.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {label}
          <LinkArrow />
        </a>
      );
    } else {
      parts.push(
        <Link key={key++} to={url} className={linkClass}>
          {label}
          <LinkArrow />
        </Link>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const calloutStyles = {
  info: { wrap: "bg-[#EBF7FE] border-[#3AB1F6]", label: "Note" },
  tip: { wrap: "bg-[#EFFaf3] border-[#34C759]", label: "Tip" },
  warning: { wrap: "bg-[#FFF7EB] border-[#F5A623]", label: "Heads up" },
};

const FaqRow = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#0000001A] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-inter font-medium text-[1.05rem] cursor-pointer"
      >
        <span>{q}</span>
        <FiChevronDown
          className={`flex-none size-5 text-[#3AB1F6] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 -mt-1 font-inter text-[#00000099] leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
};

const Block = ({ block }) => {
  switch (block.type) {
    case "h":
      return (
        <h2 className="font-jakarta font-semibold text-[1.4rem] md:text-[1.6rem] mt-8 mb-1">
          {block.text}
        </h2>
      );

    case "p":
      return (
        <p className="font-inter text-[1.05rem] md:text-[1.125rem] leading-relaxed text-[#00000099]">
          {renderInline(block.text)}
        </p>
      );

    case "list":
      return (
        <ul className="flex flex-col gap-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 font-inter text-[1.05rem] leading-relaxed">
              <span className="mt-2.5 flex-none size-1.5 rounded-full bg-[#3AB1F6]" />
              <span className="text-[#00000099]">{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );

    case "steps":
      return (
        <ol className="flex flex-col gap-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="flex-none size-7 rounded-full bg-[#3AB1F6] text-white font-inter font-semibold text-[0.9rem] flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="font-inter text-[1.05rem] leading-relaxed pt-0.5">
                {renderInline(item)}
              </span>
            </li>
          ))}
        </ol>
      );

    case "callout": {
      const style = calloutStyles[block.variant] || calloutStyles.info;
      return (
        <div className={`border-l-4 rounded-r-xl px-5 py-4 ${style.wrap}`}>
          <div className="font-inter font-semibold text-[0.95rem] text-[#000000AA] mb-1">
            {style.label}
          </div>
          <div className="font-inter text-[1.05rem] leading-relaxed text-[#00000099]">{renderInline(block.text)}</div>
        </div>
      );
    }

    case "faq":
      return (
        <div className="flex flex-col gap-3">
          {block.items.map((item, i) => (
            <FaqRow key={i} q={item.q} a={item.a} />
          ))}
        </div>
      );

    default:
      return null;
  }
};

const HelpBlocks = ({ blocks = [] }) => (
  <div className="flex flex-col gap-4">
    {blocks.map((block, i) => (
      <Block key={i} block={block} />
    ))}
  </div>
);

export default HelpBlocks;
